import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "../lib/cn.js";

/**
 * Transient confirmations — "Invite sent", "Name saved".
 *
 * NOTHING IN FEATURE 004 RAISES ONE. It is built because roadmap §004 lists it and household
 * management in 005 is the consumer; that tension with the roadmap's own "infrastructure lands just
 * before its consumer" rule is recorded in the spec's Assumptions rather than quietly resolved.
 *
 * WHAT A TOAST IS FOR, AND WHAT IT IS NOT
 *
 * A toast confirms something that already succeeded. It must never be the only place an ERROR is
 * shown: it disappears on a timer, it can be missed entirely, and a member who looks away loses the
 * message with no way to retrieve it. Failures belong in `ErrorState`, next to the thing that
 * failed, where they persist and can be read at leisure. This component deliberately offers no
 * "error" variant, so that decision does not get made casually at a call site.
 *
 * `role="status"` with `aria-live="polite"`: announced, but never interrupting whatever a screen
 * reader is in the middle of.
 */

interface ToastMessage {
  id: number;
  text: string;
}

interface ToastApi {
  /** Show a confirmation. Returns nothing — a toast is fire-and-forget by nature. */
  notify: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long a message stays. Long enough to read a short sentence without being asked to hurry. */
const DISMISS_AFTER_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  // A monotonic counter rather than Date.now(): two notifications raised in the same millisecond
  // would collide on a timestamp, and React would treat them as one key.
  const nextId = useRef(0);

  const notify = useCallback((text: string) => {
    const id = nextId.current++;
    setMessages((current) => [...current, { id, text }]);
    setTimeout(() => {
      setMessages((current) => current.filter((m) => m.id !== id));
    }, DISMISS_AFTER_MS);
  }, []);

  // Memoised so every consumer of the context does not re-render whenever a message is added.
  const api = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-6"
        role="status"
        aria-live="polite"
      >
        {messages.map((message) => (
          <Toast key={message.id}>{message.text}</Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    // Loud rather than a silent no-op. A toast that never appears is invisible in testing and in
    // review, and the cause — a missing provider — is not something a caller can guess at.
    throw new Error("useToast must be used inside a <ToastProvider>.");
  }
  return api;
}

/**
 * The visual element. Exported for composition and for stories; most callers want `useToast`.
 *
 * `pointer-events-auto` reverses the container's `pointer-events-none` — the stack spans the
 * viewport and would otherwise swallow every click along the bottom of the page.
 */
export function Toast({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div
      className={cn(
        "pointer-events-auto rounded-control border border-input bg-panel px-4 py-2 text-sm text-ink",
        "shadow-lg backdrop-blur-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}
